import type { FlagName } from "components/types/flags-and-experiments";
import { useCallback, useState, useEffect } from "react";
import FlagActions from "actions/flags-and-experiments-actions";
import { useAppDispatch, useAppSelector } from "../../../util/store-types";

/**
 * sbenedict 8/2024
 * This does not fetch the selected flag, it just provides it if it's already in Redux.
 * You probably want to be using useOnLoadFlagOrExperiment, which also requests the flag.
 */
export function useFlagOrExperiment(requestedFlagOrExperiment: FlagName) {
  const flagValue = useAppSelector(state => state.flagsAndExperiments?.[requestedFlagOrExperiment]);

  return flagValue;
}

/**
 * For the extremely common paradigm where you need to fetch a flag, then update once it's populated in Redux.
 * This also prevents unnecessary requests for flags that already exist in Redux.
 */
export function useOnLoadFlagOrExperiment(requestedFlagOrExperiment: FlagName) {
  const dispatch = useAppDispatch();
  const fetchFlag = useCallback(
    () => dispatch(FlagActions.fetchFlagOrExperiment(requestedFlagOrExperiment)),
    [dispatch, requestedFlagOrExperiment]
  );

  const flagValue = useFlagOrExperiment(requestedFlagOrExperiment);
  const [flagHasLoaded, setFlagHasLoaded] = useState(false);

  const hasLoaded = typeof flagValue !== "undefined";

  useEffect(() => {
    if (!hasLoaded) {
      fetchFlag();
    } else {
      setFlagHasLoaded(true);
    }
  }, [fetchFlag, hasLoaded]);

  useEffect(() => {
    if (hasLoaded) {
      setFlagHasLoaded(true);
    }
  }, [hasLoaded]);

  return [flagValue, flagHasLoaded];
}
